/**
 * Corpus retrieval service (PROD-574).
 *
 * Phase 1 RAG floor per master prompt v3.3 §HC8: "the corpus is the floor;
 * the learning loop is layered on top." This service exposes a single
 * `retrieveSimilar(query, k, threshold)` function that embeds the query
 * via OpenAI `text-embedding-3-small` (1536 dims) and runs cosine
 * similarity against the `corpus_chunks` table provisioned in
 * `corpus/schema.sql`.
 *
 * Architecture:
 *   - The function depends on two injected interfaces (`Embedder` and
 *     `PgQueryRunner`) so unit tests can supply hand-built fakes without
 *     installing `pg` or `openai`.
 *   - The default factory `createDefaultRetrievalDeps()` resolves real
 *     OpenAI + pg clients via dynamic import; if either is missing it
 *     returns null so call sites surface a Live Mode failure rather than
 *     crashing (HC4 + HC8).
 *   - Tenant-scoping is enforced via the `corpus` filter on the SQL.
 *     Phase 1 ships only `phase-1-seed`; the parameter is exposed for
 *     forward compatibility.
 *
 * HC5: zero em / en dashes anywhere.
 */

export interface RetrievedChunk {
  chunk_id: string;
  source_id: string;
  source_citation: string;
  section: string | null;
  tier: string;
  text: string;
  relevance_score: number;
  retrieved_at: string;
}

export interface Embedder {
  embed(text: string): Promise<number[]>;
}

/**
 * Minimal PG runner. The shape is `{ query }` so a `pg.Client` instance
 * satisfies it directly and tests can pass a `vi.fn`. Returns the raw
 * row objects we need; the service does not require typed pg results.
 */
export interface PgQueryRunner {
  query(
    text: string,
    values: unknown[],
  ): Promise<{ rows: PgChunkRow[] }>;
}

export interface PgChunkRow {
  chunk_id: string;
  source_url: string | null;
  source_citation: string;
  section: string | null;
  tier: string;
  content: string;
  retrieved_at: string | Date;
  distance: number; // cosine distance ([0, 2]); convert to relevance via 1 - d
}

export interface RetrievalDeps {
  embedder: Embedder;
  pg: PgQueryRunner;
}

export interface RetrievalOptions {
  /** Number of chunks to consider before threshold filtering. Default 5 (per SPEC §7.3). */
  k?: number;
  /** Minimum relevance score. Below this, the chunk is dropped (per SPEC §7.3). Default 0.55. */
  threshold?: number;
  /** Corpus tag scope. Default `phase-1-seed` (the only tag in Phase 1). */
  corpus?: string;
}

/**
 * SQL shape: order by cosine distance ascending; return the k closest.
 * Threshold filtering happens in TS so the test can inspect the full
 * distance distribution.
 */
export const RETRIEVE_SQL = `
SELECT
  chunk_id,
  source_url,
  source_citation,
  section,
  tier,
  content,
  retrieved_at,
  (embedding <=> $1::vector) AS distance
FROM corpus_chunks
WHERE corpus = $2
ORDER BY embedding <=> $1::vector ASC
LIMIT $3;
`;

const DEFAULT_K = 5;
const DEFAULT_THRESHOLD = 0.55;
const DEFAULT_CORPUS = 'phase-1-seed';

/**
 * Convert a numeric vector to a pgvector literal. Kept identical to the
 * helper in `scripts/ingest-corpus.ts` so wire format does not drift.
 */
export function formatPgVectorLiteral(vec: number[]): string {
  return `[${vec.map((n) => n.toString()).join(',')}]`;
}

/**
 * Convert a row's cosine distance to a relevance score in [0, 1].
 * pgvector's `<=>` returns cosine distance: 0 = identical, 1 = orthogonal,
 * 2 = opposite. We map distance to relevance via `1 - d` clamped to
 * [0, 1] so above-threshold filtering is intuitive.
 */
export function distanceToRelevance(distance: number): number {
  const r = 1 - distance;
  if (r < 0) return 0;
  if (r > 1) return 1;
  return r;
}

/**
 * Pure transformation from a row + computed relevance to the public
 * RetrievedChunk shape. Exposed so tests can assert on the mapping
 * without spinning up the full retrieval flow.
 */
export function rowToChunk(row: PgChunkRow): RetrievedChunk {
  const retrievedAt =
    row.retrieved_at instanceof Date
      ? row.retrieved_at.toISOString()
      : row.retrieved_at;
  return {
    chunk_id: row.chunk_id,
    source_id: row.source_url ?? row.source_citation,
    source_citation: row.source_citation,
    section: row.section,
    tier: row.tier,
    text: row.content,
    relevance_score: distanceToRelevance(row.distance),
    retrieved_at: retrievedAt,
  };
}

/**
 * Embed `query`, run the cosine-similarity SQL, threshold + return.
 *
 * Test path: pass `deps` with mocked `embedder` + `pg`. Production path:
 * resolve via `createDefaultRetrievalDeps()` and pass to this function.
 */
export async function retrieveSimilar(
  query: string,
  deps: RetrievalDeps,
  options: RetrievalOptions = {},
): Promise<RetrievedChunk[]> {
  if (typeof query !== 'string' || query.trim().length === 0) {
    throw new Error('[corpus-retrieval] query must be a non-empty string');
  }
  const k = options.k ?? DEFAULT_K;
  const threshold = options.threshold ?? DEFAULT_THRESHOLD;
  const corpus = options.corpus ?? DEFAULT_CORPUS;

  const queryEmbedding = await deps.embedder.embed(query);
  if (!Array.isArray(queryEmbedding) || queryEmbedding.length !== 1536) {
    throw new Error(
      `[corpus-retrieval] embedder returned unexpected shape (length=${queryEmbedding?.length ?? 'n/a'})`,
    );
  }
  const literal = formatPgVectorLiteral(queryEmbedding);
  const result = await deps.pg.query(RETRIEVE_SQL, [literal, corpus, k]);
  const chunks = result.rows.map(rowToChunk);
  return chunks.filter((c) => c.relevance_score >= threshold);
}

/**
 * Resolve real OpenAI + pg clients via dynamic import. Returns `null`
 * when either dep is missing OR credentials are absent so call sites can
 * fall back to a Live Mode failure card instead of crashing the app.
 */
export async function createDefaultRetrievalDeps(
  env: NodeJS.ProcessEnv = (typeof process !== 'undefined' ? process.env : {}) as NodeJS.ProcessEnv,
): Promise<RetrievalDeps | null> {
  const apiKey = env.OPENAI_API_KEY;
  const databaseUrl = env.DATABASE_URL;
  if (!apiKey || !databaseUrl) {
    console.warn('[corpus-retrieval] missing credentials; deps unavailable');
    return null;
  }

  // Indirect module specifiers prevent Vite's static analyzer from
  // attempting to bundle the optional `openai` / `pg` deps at test time.
  // Real ingestion runs under Node where `await import(<runtime string>)`
  // resolves correctly via Node's loader.
  const dynamicImport = (mod: string) => import(/* @vite-ignore */ mod);
  let openaiMod: unknown;
  let pgMod: unknown;
  try {
    openaiMod = await dynamicImport('openai');
  } catch {
    console.warn(
      '[corpus-retrieval] missing `openai` dep - INFRA GATE follow-up to add to package.json',
    );
    return null;
  }
  try {
    pgMod = await dynamicImport('pg');
  } catch {
    console.warn(
      '[corpus-retrieval] missing `pg` dep - INFRA GATE follow-up to add to package.json',
    );
    return null;
  }

  type OpenAILike = new (config: { apiKey: string }) => {
    embeddings: {
      create(input: { input: string; model: string }): Promise<{ data: Array<{ embedding: number[] }> }>;
    };
  };
  const Client =
    (openaiMod as { default?: OpenAILike }).default ??
    (openaiMod as unknown as OpenAILike);
  const openaiClient = new Client({ apiKey });
  const embedder: Embedder = {
    async embed(text) {
      const res = await openaiClient.embeddings.create({
        input: text,
        model: 'text-embedding-3-small',
      });
      return res.data[0]?.embedding ?? [];
    },
  };

  type PgClientCtor = new (cfg: { connectionString: string }) => {
    connect(): Promise<void>;
    query(text: string, values?: unknown[]): Promise<{ rows: PgChunkRow[] }>;
    end(): Promise<void>;
  };
  const PgClient =
    (pgMod as unknown as { Client?: PgClientCtor }).Client ??
    (pgMod as { default?: { Client: PgClientCtor } }).default?.Client;
  if (!PgClient) {
    console.warn('[corpus-retrieval] `pg` loaded but Client export not found');
    return null;
  }
  const pgClient = new PgClient({ connectionString: databaseUrl });
  await pgClient.connect();

  const pg: PgQueryRunner = {
    async query(text, values) {
      const result = await pgClient.query(text, values);
      return { rows: result.rows };
    },
  };

  return { embedder, pg };
}
